import React from 'react';

import { CircularProgress } from '@mui/material';
import { MAIN_THEME } from '@styles';
import { isEmpty } from '@utils/validationUtils';

import FormFieldWrapper from './FormFieldWrapper';
import TextAreaFormField from './TextAreaFormField';
import { IFormFieldInputBaseProps } from '.';

import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

export interface ITextInputFormFieldProps extends IFormFieldInputBaseProps {
  value: string;
  loader?: boolean;
  multiline?: boolean;
  hideError?: boolean;
  rtlContent?: boolean;
  classes?: {
    labelWrapper?: string;
    label?: string;
    input?: string;
  };
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onFocus?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
}

const useStyles = makeStyles<{ rtlContent: boolean }>()(
  (theme, { rtlContent }) => ({
    input: {
      fontSize: MAIN_THEME.fontSize.sm,
      lineHeight: '15px',
      height: 35,
      width: '100%',
      padding: '0 15px',
      margin: '0 auto',
      backgroundColor: MAIN_THEME.color.white98,
      border: `1px solid ${MAIN_THEME.color.white86}`,
      boxSizing: 'border-box',
      borderRadius: '5px',
      '&:disabled': {
        cursor: 'not-allowed',
        color: 'rgba(0, 0, 0, 0.38)!important'
      },
      '&::placeholder': {
        fontStyle: 'italic',
        fontFamily: MAIN_THEME.font.regular,
        color: MAIN_THEME.color.white86,
        opacity: 1,
        fontSize: MAIN_THEME.fontSize.sm
      },
      fontFamily: MAIN_THEME.font.regular,
      '&:focus': {
        outline: 'none'
      },
      '::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0
      }
    },
    inputError: {
      borderColor: `${MAIN_THEME.color.red} !important`,
      outlineColor: MAIN_THEME.color.red
    },
    inputContainer: {
      position: 'relative'
    },
    loader: {
      position: 'absolute',
      top: 'calc(50% - 10px)',
      right: '3%',
      opacity: 1,
      height: '25px',
      width: '25px'
    },
    materialInput: {
      backgroundColor: 'unset',
      border: 'none',
      borderBottom: `1px solid ${MAIN_THEME.color.white86}`,
      color: MAIN_THEME.color.black,
      margin: '0 auto',
      borderRadius: 0,
      padding: '10px 15px 4px 4px',
      fontSize: `${MAIN_THEME.fontSize.sm}`,
      MozAppearance: 'textfield'
    },
    disabledInput: {
      '& .MuiInputBase-root.Mui-disabled': {
        color: 'rgba(0, 0, 0, 0.5)!important',
        WebkitTextFillColor: 'rgba(0, 0, 0, 0.5)!important'
      },
      '& .MuiInputBase-input.Mui-disabled': {
        color: 'rgba(0, 0, 0, 0.5)!important',
        WebkitTextFillColor: 'rgba(0, 0, 0, 0.5)!important'
      }
    }
  })
);

const TextInputFormField: React.FC<ITextInputFormFieldProps> = ({
  error,
  name,
  title,
  value = '',
  placeholder,
  disabled,
  material,
  loader,
  inputOnly,
  hideError,
  rtlContent,
  fullWidth,
  multiline = false,
  classes = {},
  hidden,
  onChange,
  onFocus,
  onBlur
}) => {
  if (hidden) return null;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { classes: inputClasses } = useStyles({ rtlContent });

  const renderInput = () =>
    multiline ? (
      <TextAreaFormField
        name={name}
        title={disabled && !isEmpty(value) ? value.toString() : title}
        value={value}
        onChange={(e) => onChange(e)}
        onBlur={(e) => onBlur(e)}
        disabled={disabled || loader}
        placeholder={placeholder}
        error={error}
      />
    ) : (
      <div className={inputClasses.inputContainer}>
        <input
          className={clsx(inputClasses.input, {
            [inputClasses.materialInput]: material,
            [inputClasses.inputError]: error,
            [inputClasses.disabledInput]: disabled,
            [classes.input]: classes.input
          })}
          title={disabled && !isEmpty(value) ? value.toString() : title}
          id={name}
          name={name}
          aria-invalid={!!error}
          aria-describedby={`errorMsg-${name}`}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          type={'text'}
          disabled={disabled}
          placeholder={placeholder}
        />
      </div>
    );

  return inputOnly || multiline ? (
    renderInput()
  ) : (
    <FormFieldWrapper
      title={title}
      error={error}
      name={name}
      fullWidth={fullWidth}
      className={classes?.labelWrapper}
      material={material}
      hideError={hideError}
    >
      {renderInput()}
      {loader && (
        <CircularProgress
          className={inputClasses.loader}
          size={20}
          thickness={3}
        />
      )}
    </FormFieldWrapper>
  );
};

export default TextInputFormField;
